import { useViewjobEmployerDossierQuery } from "./__generated__/viewjobEmployerDossier.gqlgenerated";
import { FloatingAskCareerScoutStarters } from "./components/core/AskCareerScoutStarters/FloatingAskCareerScoutStarters";
import {
  CAREER_SCOUT_ALWAYS_AWARE_BUCKETS,
  useCareerScoutAlwaysAwareBucket,
} from "./components/core/AskCareerScoutStarters/proctor";
import { EmployerInsightsPopoverProvider } from "./components/core/JobComponent/EmployerInsights/EmployerInsightsPopover";
import {
  StickyButtonAccessory,
  StickyButtonsRoot,
} from "./components/core/JobComponent/StickyButtons/StickyButtons";
import { OGWrappedViewJobMainContent } from "./components/core/JobComponent/ViewJobMainContent/ViewJobMainContent";
import { WebToAppPromo } from "./components/core/WebToAppPromo";
import { ApplicantInsightsModalContextProvider } from "./components/mosaic/ApplicantInsights/context/ApplicantInsightsModalContextProvider";
import { DwellTimingProvider } from "./context/DwellTiming";
import { EmployerOutreachProvider } from "./context/EmployerOutreachContext";
import {
  useViewjobOneGraphModel,
  useViewjobSourceUrl,
  useProctorGroups,
} from "./context/ViewJobDataContext";
import { useConversationStarterCleanup } from "./hooks/useConversationStarterCleanup";
import { isJobFromEmployerOutreach } from "./hooks/useEmployerOutreachSource";
import { CareerScoutLoggingContextProvider } from "../../ace/logging/CareerScoutLoggingContext";
import { getDeviceProperties } from "../../shared/logging/LoggableDeviceProperties";
import { VisibilityTrackerProvider } from "../../shared/logging/visibility-tracker";
import { useTareData } from "../../shared/native-modules/tare/TareDataContext";
import { useRunOnceAfterMount } from "../../shared/util/useRunOnceAfterMount";

import { log as logRNViewJobPerformance } from "@indeed/signals-schema/com/indeed/signals/schema/reactnative/RNViewJobPerformance";
import {
  AvroOptional,
  log as logMobPageLoadInfo,
  ScreenOrientationV3,
} from "@indeed/signals-schema/com/indeed/signals/schema/viewjob/RNMobPageLoadInfoV3";
import React from "react";
import { useWindowDimensions } from "react-native";

export interface Props {
  url?: string | null;
  reload: () => void;
  navigationStartTimeMs: number;
  fetchStartTimeMs: number | null;
  fetchEndTimeMs: number | null;
}

function ModalContextProvider({ children }: { children: React.ReactNode }) {
  return (
    <ApplicantInsightsModalContextProvider>
      <EmployerInsightsPopoverProvider>{children}</EmployerInsightsPopoverProvider>
    </ApplicantInsightsModalContextProvider>
  );
}

export function AppVJLoadedContent({
  reload,
  navigationStartTimeMs,
  fetchStartTimeMs,
  fetchEndTimeMs,
}: Props): React.JSX.Element {
  const { height, width, scale } = useWindowDimensions();
  const ogModel = useViewjobOneGraphModel();
  const tareData = useTareData();
  const isAppBtnSticky = ogModel?.isAppBtnSticky ?? true;
  const isScrollVariant =
    useCareerScoutAlwaysAwareBucket() === CAREER_SCOUT_ALWAYS_AWARE_BUCKETS.SCROLL;

  const url = useViewjobSourceUrl();
  const proctorGroups = useProctorGroups();
  // Use ogModel as the primary interface for jobKey and mobvjtk
  const jobKey = ogModel?.job.jobKey;
  const mobvjtk = ogModel?.mobvjtk;
  const isFromEmployerOutreach = isJobFromEmployerOutreach(url ?? "");

  useConversationStarterCleanup();

  useViewjobEmployerDossierQuery({
    variables: {
      input: jobKey || "",
    },
    skip: !jobKey || !isFromEmployerOutreach,
  });

  useRunOnceAfterMount(() => {
    if (!ogModel) {
      return;
    }

    const country = ogModel.features.country;
    const ctk = ogModel.ctk;
    const mobtk = ogModel.mobvjtk;
    const deviceProperties = getDeviceProperties();
    const now = Date.now();

    logRNViewJobPerformance({
      ctk: AvroOptional.string(ctk ?? null),
      deviceProperties,
      baseMetrics: {
        timeToInteractiveMs: AvroOptional.long(now - navigationStartTimeMs),
      },
      fetchStartMs: fetchStartTimeMs
        ? AvroOptional.long(fetchStartTimeMs - navigationStartTimeMs)
        : AvroOptional.long(null),
      fetchEndMs: fetchEndTimeMs
        ? AvroOptional.long(fetchEndTimeMs - navigationStartTimeMs)
        : AvroOptional.long(null),
      grps: AvroOptional.string(proctorGroups),
    });

    logMobPageLoadInfo({
      deviceProperties: deviceProperties,
      rnAppBundleVersion: AvroOptional.string(deviceProperties.rnAppBundleVersion),
      mobvjtk: AvroOptional.string(mobtk),
      jsvPage: AvroOptional.string("mvjjsv"),
      country: AvroOptional.string(country),
      ctk: AvroOptional.string(ctk),
      mobtk: AvroOptional.string(mobtk),
      scrWidth: AvroOptional.long(width),
      scrHeight: AvroOptional.long(height),
      scrRes: AvroOptional.string(`${Math.floor(width)}x${Math.floor(height)}`),
      pixelRatio: AvroOptional.float(scale),
      scrOrientation: {
        ScreenOrientationV3:
          height > width ? ScreenOrientationV3.PORTRAIT : ScreenOrientationV3.LANDSCAPE,
      },
    });
  });
  return (
    <CareerScoutLoggingContextProvider
      accountId={tareData?.accountStatus?.accountId ?? null}
      proctorGroups={proctorGroups ?? null}
      ctk={ogModel?.ctk ?? null}
    >
      <ModalContextProvider>
        <DwellTimingProvider
          tk={mobvjtk || null}
          pgtype="mobvj"
          accountId={tareData?.accountStatus?.accountId || null}
          jobKey={jobKey || null}
          hasCompanySummary={!!ogModel?.employerInsightsModel?.companySummary}
        >
          {/* Make sure VisibilityProvider is the parent of StickyButtonRoot, since it expects a ScrollView as a descendent */}
          <VisibilityTrackerProvider viewportDimensions={{ width, height }}>
            <EmployerOutreachProvider>
              <StickyButtonsRoot needSticky={isAppBtnSticky}>
                <OGWrappedViewJobMainContent reload={reload} />
                {isScrollVariant && (
                  <StickyButtonAccessory>
                    <FloatingAskCareerScoutStarters />
                  </StickyButtonAccessory>
                )}
              </StickyButtonsRoot>
            </EmployerOutreachProvider>
          </VisibilityTrackerProvider>
          <WebToAppPromo />
        </DwellTimingProvider>
      </ModalContextProvider>
    </CareerScoutLoggingContextProvider>
  );
}
